// Configuracion OIDC con soporte runtime (window.__ENV__)
import { WebStorageStateStore } from 'oidc-client-ts';
import { ENV } from '../config/env';

const margenSegundos = Number.parseInt(ENV.OIDC_TOKEN_EXPIRING_NOTIFICATION_S, 10) || 300;

/**
 * Sin `offline_access` no hay refresh token y `signinSilent` cae al iframe, que sin
 * `silent_redirect_uri` tira "No silent_redirect_uri configured" — y contra Azure AD fallaría
 * igual por el bloqueo de cookies de terceros.
 *
 * <p>Es una falla de configuración silenciosa de las peores: no rompe nada al arrancar, la sesión
 * simplemente se muere a la hora y nadie sabe por qué. Que se vea (#168).
 *
 * <p>Se discrimina por IdP a propósito, porque `offline_access` no significa lo mismo en los dos:
 * en Azure AD es "dame un refresh token" y es obligatorio para que esto funcione; en Keycloak abre
 * además una <b>offline session</b> (30 días de idle en los realms de este repo) y ya devuelve
 * refresh token sin pedirlo, así que omitirlo puede ser una decisión deliberada de política de
 * sesión y no un olvido.
 */
const verificarScope = (authority, scope) => {
  if (/\boffline_access\b/.test(scope || '')) return;
  const esAzure = /login\.microsoftonline\.com/.test(authority || '');
  const mensaje =
    '[OIDC] OIDC_SCOPE no incluye offline_access: la renovación silenciosa de la sesión no va a '
    + 'funcionar y los usuarios van a perder la sesión al vencer el token.';
  if (esAzure) console.error(mensaje);
  else console.warn(`${mensaje} Con Keycloak puede ser deliberado — verificá la política de sesión del realm.`);
};

verificarScope(ENV.OIDC_AUTHORITY, ENV.OIDC_SCOPE);

export const oidcConfig = {
    authority: ENV.OIDC_AUTHORITY,
    client_id: ENV.OIDC_CLIENT_ID,
    redirect_uri: ENV.OIDC_REDIRECT_URI,
    post_logout_redirect_uri: ENV.OIDC_POST_LOGOUT_URI,
    response_type: 'code',
    scope: ENV.OIDC_SCOPE,
    loadUserInfo: false,
    automaticSilentRenew: true,

    // El default de oidc-client-ts son 60 s. El SilentRenewService sólo reprograma ante
    // ErrorTimeout y con 5 s de espera, así que con un margen chico un fallo transitorio de red
    // se come toda la ventana y el token vence. Ver la regla en config/env.js.
    accessTokenExpiringNotificationTimeInSeconds: margenSegundos,

    // Explícito porque su consecuencia importa: sin monitoreo de sesión, `addUserSignedOut` nunca
    // dispara y un cierre de sesión hecho desde el IdP tarda hasta un ciclo de renovación en
    // notarse. Se deja en false porque Azure AD v2 no publica `check_session_iframe` (Keycloak sí,
    // pero la config es compartida y el común denominador manda). El vencimiento se detecta por
    // `addAccessTokenExpired`, en useSessionExpiry.
    monitorSession: false,

    // Es el default, pero conviene que se lea: el usuario vive en sessionStorage, no en
    // localStorage. No se pasa a localStorage a propósito — no resolvería la carrera entre
    // pestañas (oidc-client-ts no tiene elección de líder) y persistiría el refresh token entre
    // reinicios del navegador, que en una bóveda documental es un retroceso.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  };
